import { DatePipe } from '@angular/common';
import { FormatNumberPipe } from '../../../../../shared/pipes/number-separator.pipe';
import { EntityPopup, PopupInfoItem } from './entity-popup.interface';

export interface WaterSupplyRegistry {
  assigned_water_supply: number;
  start_date: string;
  end_date: string;
}

export interface TomaEntity {
  diameter: string;
  hidrante_id: string;
  counter: string;
  remote: string;
  last_irrigation: number;
  last_irrigation_date: string;
  accumulated_consumption: number;
  start_date_accumulated_consumption: string;
  end_date_accumulated_consumption: string;
  current_readding: number;
  current_readding_datetime: string;
  water_supplies: {
    total_consumption: number;
    consumption_percentage: number;
    start_date: string;
    end_date: string;
    registry: WaterSupplyRegistry[];
  };
}

export class TomaPopup implements EntityPopup {
  private formatNumber = new FormatNumberPipe();
  private datePipe = new DatePipe('es-ES');

  constructor(private entity: TomaEntity) {}

  getBadgeText(): string {
    return `DN ${this.entity.diameter || '-'} - Hidrante ${this.entity.hidrante_id || '-'}`;
  }

  getCounterBadge(): string | null {
    if (!this.entity.counter) return null;
    return `Remota ${this.entity.remote || ': -'}`;
  }

  getCounterTitle(): string | null {
    if (!this.entity.counter) return null;
    return `Contador asociado ${this.entity.counter}`;
  }

  getInfoItems(): PopupInfoItem[] {
    const lastIrrigationDate = this.entity.last_irrigation_date
      ? this.datePipe.transform(
          this.entity.last_irrigation_date,
          'dd/MM/yyyy HH:mm:ss'
        )
      : '';

    const acStartDate = this.entity.start_date_accumulated_consumption
      ? this.datePipe.transform(
          this.entity.start_date_accumulated_consumption,
          'dd/MM/yyyy'
        )
      : '';

    const acEndDate = this.entity.end_date_accumulated_consumption
      ? this.datePipe.transform(
          this.entity.end_date_accumulated_consumption,
          'dd/MM/yyyy'
        )
      : '';

    const readingDate = this.entity.current_readding_datetime
      ? this.datePipe.transform(
          this.entity.current_readding_datetime,
          'dd/MM/yyyy HH:mm:ss'
        )
      : '';

    const dotacionStartDate = this.entity.water_supplies?.start_date
      ? this.datePipe.transform(
          this.entity.water_supplies.start_date,
          'dd/MM/yyyy'
        )
      : '';

    const dotacionEndDate = this.entity.water_supplies?.end_date
      ? this.datePipe.transform(
          this.entity.water_supplies.end_date,
          'dd/MM/yyyy'
        )
      : '';

    return [
      {
        icon: 'humidity_low',
        label: 'Último riego',
        value: this.entity.last_irrigation,
        unit: 'm³',
        date: lastIrrigationDate,
      },
      {
        icon: 'trending_up',
        label: 'Consumo acumulado',
        value: this.entity.accumulated_consumption,
        unit: 'm³',
        date:
          acStartDate && acEndDate ? `${acStartDate} al ${acEndDate}` : null,
      },
      {
        icon: 'readiness_score',
        label: 'Última lectura',
        value: this.entity.current_readding,
        unit: 'm³',
        date: readingDate,
      },
      {
        icon: 'humidity_mid',
        label: 'Dotación consumida',
        value:
          this.entity.water_supplies?.total_consumption != null
            ? `${this.formatNumber.transform(this.entity.water_supplies.total_consumption)} m³ (${this.formatNumber.transform(this.entity.water_supplies.consumption_percentage)}% de la dotación)`
            : null,
        date:
          dotacionStartDate && dotacionEndDate
            ? `${dotacionStartDate} al ${dotacionEndDate}`
            : null,
      },
    ];
  }

  getWaterSuppliesList(): { value: string; dateRange: string }[] {
    if (!this.entity.water_supplies?.registry) return [];

    return this.entity.water_supplies.registry.map(e => {
      const startDate = this.datePipe.transform(e.start_date, 'dd/MM/yyyy');
      const endDate = this.datePipe.transform(e.end_date, 'dd/MM/yyyy');
      return {
        value: `${this.formatNumber.transform(e.assigned_water_supply)} m³`,
        dateRange: `${startDate} al ${endDate}`,
      };
    });
  }
}
